/**
 * The global half of the keymap (`110-design-system.md` §7), and the `?` sheet it documents.
 *
 * The bindings that belong to a feature stay with that feature — the palette owns `Mod+K`, the
 * tree owns `Mod+N`, Tiptap owns `Mod+B`, the autosave owns `Mod+S`. What is left is the set
 * that belongs to no one: the leader sequences, dark mode, and the cheatsheet itself. They live
 * here, mounted once by the shell.
 *
 * `Mod+S` is bound here **as well as** in `useAutosave`, and that is not a duplicate: the
 * autosave's listener only exists while a page is open. §7 says `Mod+S` is *preventable and
 * prevented*, unconditionally — and a user who hits it on the calendar and gets the browser's
 * "Save page as…" dialog has been told, wrongly, that this app does not save their work. So the
 * binding exists on every route; on a page it flushes, everywhere else it does nothing, loudly.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { ShortcutsDialog } from '@/keyboard/ShortcutsDialog';
import { useThemePreference } from '@/features/settings/preferences';
import { lastView } from '@/features/calendar/lastView';

export function GlobalHotkeys() {
  const navigate = useNavigate();
  const { theme, setTheme } = useThemePreference();
  const [sheet, setSheet] = useState(false);

  const hotkeys: Hotkey[] = useMemo(() => {
    const go = (path: string) => () => {
      void navigate(path);
    };

    return [
      // The cheatsheet. `?` is the roadmap's binding, `Mod+/` is §7's. Both, because both are
      // written down and neither costs anything. `?` is Shift+/ on every layout that has one.
      {
        key: '?',
        shift: true,
        handler: () => {
          setSheet(true);
        },
      },
      {
        key: '/',
        mod: true,
        handler: () => {
          setSheet(true);
        },
      },

      // §7: "Toggle dark mode". Mod+D is the browser's bookmark and is deliberately NOT bound.
      // This flips between light and dark; it never lands on `system`, because a toggle that
      // sometimes changes nothing (system, on a light OS, "toggling" to light) reads as broken.
      {
        key: 'd',
        mod: true,
        shift: true,
        handler: () => {
          const resolved =
            theme === 'system'
              ? window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light'
              : theme;
          setTheme(resolved === 'dark' ? 'light' : 'dark');
        },
      },

      // §7's browser-conflict table: "Mod+S — Preventable. Prevented." Everywhere, not just on
      // a page. `useAutosave` does the actual flushing when there is something to flush.
      { key: 's', mod: true, handler: () => undefined },

      // The `G` leader (§7). Bare keys: `useHotkeys` will not fire them in a text field, which
      // is what makes `g` safe to type in a document.
      { key: 'c', leader: 'g', handler: () => void navigate(`/calendar/${lastView()}`) },
      { key: 'a', leader: 'g', handler: go('/calendar') },
      { key: 'i', leader: 'g', handler: go('/chat') },
      { key: 't', leader: 'g', handler: go('/trash') },
      { key: 's', leader: 'g', handler: go('/settings') },
    ];
  }, [navigate, setTheme, theme]);

  useHotkeys(hotkeys);

  return <ShortcutsDialog open={sheet} onOpenChange={setSheet} />;
}
